/**
 * Get Chart Point Positions Tool
 *
 * Query specific chart data point positions for callouts, labels, and alignment.
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import {
    GetChartPointPositionsParamsSchema,
    type GetChartPointPositionsParams,
    type GetChartPointPositionsResponse,
    type ChartPointSeries,
    type ChartPointPositionsResult,
    type ChartShapeInfo,
} from "./getChartPointPositions.schema";
import { LiveTrackingStorage } from "../../../../../services/tracking/liveTrackingStorage";
import { SlideCapture } from "../../../../../services/capture/slideCapture";
import { BACKEND_BASE_URL } from "../../../../../services/communication/backendClient";
import { getOrCreatedocumentId } from "../../../../../services/documentIdManager";

// ═══════════════════════════════════════════════════════════════════════════
// GET CHART POINT POSITIONS TOOL
// ═══════════════════════════════════════════════════════════════════════════

export class GetChartPointPositionsTool extends ToolBase<
    GetChartPointPositionsParams,
    GetChartPointPositionsResponse
> {
    public readonly name = "getChartPointPositions";
    public readonly description: ToolDescription;
    fieldsToCheck = [
        { name: "uiDescription", type: "string" as const, list_status: false, order: 0 },
    ];

    public readonly systemPromptDescription = `### getChartPointPositions
Get precise chart point positions for clean callouts and labels.

**When to use:**
- Labeling chart points with callouts or data labels
- Positioning arrows or markers next to data points
- Moving labels around for a cleaner, uncluttered layout
- Aligning shapes relative to chart points or chart bounds

**Important details:**
- Returned positions are chart-relative. Add the chart shape's position to get slide-absolute coordinates.
- Use the results to place labels consistently (e.g., above peaks, outside bars).

\`\`\`json
{
  "slideIndex": 5,
  "queries": [
    { "shapeName": "Chart 1", "seriesIndex": 0, "xValues": ["Q3", "Q4"] }
  ]
}
\`\`\``;

    private storage: LiveTrackingStorage;
    private slideCapture: SlideCapture;

    constructor() {
        super();
        this.storage = new LiveTrackingStorage();
        this.slideCapture = new SlideCapture(this.storage);
        this.description = new ToolDescription(
            "getChartPointPositions",
            "Query specific chart point positions for callouts, labels, and placement.",
            GetChartPointPositionsParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<GetChartPointPositionsResponse> {
        const parseResult = safeParseToolParams(GetChartPointPositionsParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(parseResult.error);
        }

        const params = parseResult.data;
        console.log(`[GetChartPointPositionsTool] Resolving chart points for slide ${params.slideIndex}`);

        // Use anchored documentId from conversation context when available
        let documentId: string;
        const injecteddocumentId = (parameters as Record<string, unknown>)?.documentId as string | undefined;
        if (injecteddocumentId) {
            documentId = injecteddocumentId;
            console.log(`[GetChartPointPositionsTool] Using anchored deck ID from conversation: ${documentId}`);
        } else {
            try {
                documentId = await getOrCreatedocumentId();
                console.log(`[GetChartPointPositionsTool] Fallback: Got deck ID from active presentation: ${documentId}`);
                console.warn("[GetChartPointPositionsTool] ⚠️ No anchored documentId in parameters - using live lookup");
            } catch (error) {
                console.error("[GetChartPointPositionsTool] Failed to get deck ID:", error);
                throw new Error("No active deck - cannot query chart points. Make sure a presentation is open.");
            }
        }

        // Initialize storage and capture
        await this.storage.initialize();
        await this.slideCapture.initialize();

        // Capture fresh data and sync to IndexedDB
        const captureResult = await this.captureAndGetSnapshot(documentId, params.slideIndex);
        if (!captureResult?.snapshot?.pptxBase64) {
            throw new Error(`Failed to capture slide ${params.slideIndex} for chart point queries.`);
        }

        const chartPointQueriesPayload = params.queries.map(query => ({
            shape_name: query.shapeName,
            series_index: query.seriesIndex ?? 0,
            x_values: query.xValues,
        }));

        const response = await fetch(`${BACKEND_BASE_URL}/slides/chart_point_positions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                pptx_base64: captureResult.snapshot.pptxBase64,
                chart_point_queries: chartPointQueriesPayload,
            }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Chart point query failed (${response.status}): ${errorText}`);
        }

        const data = await response.json();

        const results = (data.results || []) as ChartPointPositionsResult[];
        const chartShapes = (data.chartShapes || []) as ChartShapeInfo[];
        const summary = this.buildSummary(params.slideIndex, results, chartShapes);

        return {
            slideIndex: params.slideIndex,
            results,
            chartShapes,
            summary,
        };
    }

    private async captureAndGetSnapshot(documentId: string, slideIndex: number) {
        return PowerPoint.run(async (context) => {
            const slides = context.presentation.slides;
            slides.load("items");
            await context.sync();

            const idx = slideIndex - 1;
            if (idx < 0 || idx >= slides.items.length) {
                console.warn(`[GetChartPointPositionsTool] Slide index out of range: ${slideIndex}`);
                return undefined;
            }

            const targetSlide = slides.items[idx];
            return this.slideCapture.captureAndSync(context, targetSlide, idx, documentId);
        });
    }

    private buildSummary(
        slideIndex: number,
        results: ChartPointPositionsResult[],
        chartShapes: ChartShapeInfo[]
    ): string {
        const lines: string[] = [];
        lines.push(`Chart point positions for slide ${slideIndex}:`);

        if (!results.length) {
            if (chartShapes.length === 0) {
                lines.push("No chart shapes found.");
            } else {
                lines.push("No matching points found for the requested queries.");
            }
            return lines.join("\n");
        }

        for (const chart of results) {
            const chartLabel = chart.shapeName || chart.shapeId || "Unnamed chart";
            lines.push(`- ${chartLabel}:`);

            const seriesList = chart.series || [];
            for (const series of seriesList) {
                lines.push(this.formatSeries(series));
            }
        }

        return lines.join("\n");
    }

    private formatSeries(series: ChartPointSeries): string {
        const seriesLabel = series.name ? `Series ${series.si} (${series.name})` : `Series ${series.si}`;
        const points = series.pts || [];

        if (!points.length) {
            return `  - ${seriesLabel}: no points matched`;
        }

        const pointSummaries = points.map(point => {
            const cat = point.cat ?? `index ${point.i}`;
            const anchor = `(${point.anchor.x.toFixed(1)}, ${point.anchor.y.toFixed(1)})`;
            return `${cat} @ ${anchor}`;
        });

        return `  - ${seriesLabel}: ${pointSummaries.join("; ")}`;
    }
}

export default GetChartPointPositionsTool;
